import Vue from "vue";
import { Debouncer } from "../../utilities/debouncer";

const getDefaultState = () => {
  return {
    items: [],
    selectedItems: [],

    app: "" /** Type of app */,
    itemTitle: "",

    tags: [], // used for Tagger (not loading tags)
    overrideTagsShow: false,

    allDataForPropertyLoaded: false,
    switchingTag: false,

    writerToBottom: false,

    recentlyDeletedActive: false,

    propertyIDs: [], // If in any particular tag, it will contain values
    properties: [],
    selectedProperty: {},

    itemConfig: {
      position: "left" /* left / middle */,
      title: false,
      content: false,
      checkmark: false,
      tags: false,
      trashBeforeDelete: false,
    },

    itemType: "TASK",

    firstLoadDone: false,
    currentlyLoadingItems: false,
    preloadDebouncer: Debouncer(),
    trashIsActive: false,

    /* Options for getting items, could be custom loaded per tag or context! */
    options: {
      sort: -1, // 1 or -1 Default sort is chronologically first item first
      limit: 15, // Default get 20 items at a time
      skip: 0, // Change per round of queries
    },

    /**
     * editTracker is an object 'array' of objects that track the editing status of a particular item.
     * It is split up so that when switching between editing multiple objects, no data loss occurs.
     *
     * Schema for editTracker is as follows:
     * [{
     *  "itemID": {
     *    editing: Boolean,
     *    savingInProgress: Boolean,
     *    savingFailed: Boolean,
     *    recentlyChangedFields: [],
     *    backupValue: {},
     *  }
     * }]
     */
    editTracker: {},

    creator: {
      currentSelectedType: "TASK",
      TASK: "",
    },

    viewerCardOptions: {
      title: true,
      tags: true,
    },
  };
};

const state = Vue.observable(getDefaultState());

function newTrackerObject(item) {
  return {
    editing: false,
    savingInProgress: false,
    savingFailed: false,
    recentlyChangedFields: [],
    item, // Used for backups
    savingQueue: [],

    itemTagger: {
      componentThatCalledTagger: "", // e.g. "ItemListCard" or "ItemViewerCard"
      tagName: "",
      selectedSuggestedTagID: "", // In case existing tag is selected from suggested tags
    },

    /** Initialize debouncer function for the item. Ensures managing the same item. */
    debounce: Debouncer(),
  };
}

const methods = {
  resetStore() {
    Object.assign(state, Vue.observable(getDefaultState()));
  },

  /** ========== ITEM MANAGER ========== */
  setCurrentlyLoadingItems(newState) {
    state.currentlyLoadingItems = newState;
  },

  addItemsToItems(items) {
    state.items = [...state.items, ...items];
  },

  syncItemsSkipValue() {
    state.options.skip = state.items.length;
  },

  /** During first load, marks first load as done and
   * open the first item in the list. */
  handleItemsFirstLoad() {
    /** If first load already done, cancel function. */
    if (state.firstLoadDone) return;

    /** Mark first load as done. */
    state.firstLoadDone = true;

    /** Select first item in list to open it */
    if (state.items.length) {
      state.selectedItems = [state.items[0]];
    }
  },

  addOneItemToList(item) {
    state.items.unshift(item);
  },

  addOneItemBackToList(backupData) {
    state.items.splice(backupData.index, 0, backupData.item);
  },

  /*   removeOneItemFromTrash(item) {},

  addOneItemBackToTrash(backupData) {}, */

  replaceOneItemInList(itemID, newItem) {
    /** Find index of item to replace. */
    const itemIndex = state.items.findIndex((obj) => obj._id === itemID);

    /** Ensure item exists in array.  */
    if (itemIndex !== -1) state.items.splice(itemIndex, 1, newItem);
  },

  replaceManyItemsInList(items) {
    for (var i = 0; i < items.length; i++) {
      const item = items[i];
      const itemID = item._id;
      /** If currently selected, user may be editing & already has newer version */
      if (state.selectedItems.find((o) => o._id === itemID)) continue;

      /** Replace the item in list */
      ItemStore.methods.replaceOneItemInList(itemID, item);
    }
  },

  removeOneItemFromList(itemID) {
    /** Find index of item to remove. */
    const itemIndex = state.items.findIndex((obj) => obj._id === itemID);

    /** Ensure item exists in array.  */
    if (itemIndex !== -1) state.items.splice(itemIndex, 1);
  },

  /** ========== ITEM CREATOR ========== */
  syncCreatorInput(value, itemType) {
    if (Object.hasOwn(state.creator, itemType)) {
      state.creator[itemType] = value;
    }
  },

  clearCreatorInput(itemType) {
    if (Object.hasOwn(state.creator, itemType)) {
      state.creator[itemType] = "";
    }
  },

  /** ====================================================================== *
   * EDITING TRACKER ==================================================
   * ====================================================================== */

  removeTag(itemID, tagIndex) {
    this.tags.splice(tagIndex, 1);
    console.log(state, itemID);
  },

  removeLastTag(tagData) {
    const { itemID } = tagData;
    /* Don't remove last tag if currently text input has text */
    if (!this.tag) {
      this.removeTag(this.tags.length - 1);
    }
    console.log(state, itemID);
  },

  /** ========== ITEM TAGGER MANAGER ========== */
  rejectIfItemNotTracked(itemID) {
    if (!Object.hasOwn(state.editTracker, itemID)) {
      throw new Error("Failed to toggle item tagger. Item not found in editTracker.");
    }
  },
  /**
   * @param {Object} itemData
   * @param {String} itemData.itemID ID of the item to toggle the tagger for
   * @param {String} itemData.componentThatCalledTagger Where the tagger is being opened from (e.g. "ItemViewerCard")
   */
  toggleItemTagger(itemID, componentThatCalledTagger) {
    this.rejectIfItemNotTracked(itemID);

    if (!state.editTracker[itemID].itemTagger.componentThatCalledTagger) {
      state.editTracker[itemID].itemTagger.componentThatCalledTagger = componentThatCalledTagger;
    } else {
      state.editTracker[itemID].itemTagger.componentThatCalledTagger = "";
    }
  },

  closeItemTagger(itemID) {
    this.rejectIfItemNotTracked(itemID);
    state.editTracker[itemID].itemTagger.componentThatCalledTagger = "";
    state.editTracker[itemID].itemTagger.tagName = "";
    state.editTracker[itemID].itemTagger.selectedSuggestedTagID = "";
  },

  closeAllItemTaggers() {
    for (const itemID in state.editTracker) {
      state.editTracker[itemID].itemTagger.componentThatCalledTagger = "";
      state.editTracker[itemID].itemTagger.tagName = "";
      state.editTracker[itemID].itemTagger.selectedSuggestedTagID = "";
    }
  },

  syncEditTrackerTagName(itemID, tagName) {
    this.rejectIfItemNotTracked(itemID);
    state.editTracker[itemID].itemTagger.tagName = tagName;
  },

  addTagToItem(itemID, tag) {
    /** Find item by itemID and add the tag to the item's properties. */
    const item = state.items.find((obj) => obj._id === itemID);

    /** Throw error if item not found. */
    if (!item) throw new Error("Failed to add tag to item. Item not found in items array.");

    /** Add the new tag to the item's properties field if it exists, else create the
     * properties field with the new tag inside it (using Vue.set to maintain reactivity). */
    Object.hasOwn(item, "properties") ? item.properties.push(tag) : Vue.set(item, "properties", [tag]);
  },

  replaceOneItemTag(itemID, oldTagID, newTag) {
    /** Find item by itemID and add the tag to the item's properties. */
    const item = state.items.find((obj) => obj._id === itemID);

    /** Throw error if item not found. */
    if (!item) throw new Error("Failed to replace tag on item. Item not found in items array.");

    /** Reject if item has no properties field or has no properties*/
    if (!Object.hasOwn(item, "properties") || !item.properties.length) return;

    /** Find index of tag to replace. */
    const tagIndex = item.properties.findIndex((obj) => obj._id === oldTagID);

    /** Throw error if tag is not found. */
    if (tagIndex === -1) throw new Error("Failed to replace tag on item. Tag not found in item's properties.");

    /** Replace the tag in the item's properties field. */
    item.properties.splice(tagIndex, 1, newTag);
  },

  removeOneTagFromItem(itemID, tagID) {
    /** Find item by itemID and add the tag to the item's properties. */
    const item = state.items.find((obj) => obj._id === itemID);

    /** Reject if item is not found. */
    if (!item) throw new Error("Failed to remove tag from item. Item not found in items array.");

    /** Reject if item has no properties field or has no properties*/
    if (!Object.hasOwn(item, "properties") || !item.properties.length) return;

    /** Find index of tag to remove. */
    const tagIndex = item.properties.findIndex((obj) => obj._id === tagID);

    /** Reject if tag is not found. */
    if (tagIndex === -1) return;

    /** Remove the tag from the item's properties field. */
    item.properties.splice(tagIndex, 1);
  },

  clearEditTrackerTagName(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].itemTagger.tagName = "";
      state.editTracker[itemID].itemTagger.selectedSuggestedTagID = "";
    }
  },

  /**
   * Replaces all currently selected items with one newly selected item.
   * @param {Object} newSelection Newly selected Item
   */
  replaceSelectedItems(newSelection) {
    state.selectedItems = [newSelection];
  },

  completeOneItemByID(itemID) {
    const itemIndex = state.items.findIndex((item) => item._id === itemID);
    if (itemIndex !== -1) Vue.set(state.items[itemIndex], "complete", true);
  },

  uncompleteOneItemByID(itemID) {
    const itemIndex = state.items.findIndex((item) => item._id === itemID);
    if (itemIndex !== -1) Vue.set(state.items[itemIndex], "complete", false);
  },

  /** ========== ITEM EDITING STATUS ========== */
  /**
   * Initalize tracking for an item's changes if they're not yet being tracked.
   * @param {*} state
   * @param {Object} item
   */
  initItemTracking(item) {
    /** Check if item is NOT yet tracked */
    if (!Object.hasOwn(state.editTracker, item._id)) {
      /** Intialize a new tracker object for tracking item updates. */
      const tracker = newTrackerObject(item);

      /** Add the item tracker object to the editTracker object. */
      Vue.set(state.editTracker, item._id, tracker);
    } else {
      /** If item is already tracked, update tracker object with the
       * latest version of the item. */
      state.editTracker[item._id]["item"] = item;
    }
  },

  /** ========== ITEM RECENTLY EDITED FIELDS ========== */

  /**
   * Adds an item's recently changed field to change tracker.
   * @param {*} state
   * @param {
   *  itemID: String,
   *  field: String,
   * } changeData Changed item's ID and the field name that changed
   */
  updateRecentlyChangedField(itemID, field) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      /** If the changed field is not yet in the recentlyChangedFields, add it. */
      if (!state.editTracker[itemID].recentlyChangedFields.includes(field)) {
        state.editTracker[itemID].recentlyChangedFields.push(field);
      }
    }
  },

  /**
   * Clears the recently chnaged fields of an item being tracked,
   * if it's being tracked.
   * @param {*} state
   * @param {String} itemID
   */
  clearRecentlyChangedFields(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].recentlyChangedFields = [];
    }
  },

  /** ========== SYNCING ITEM VALUES ========== */

  /**
   * Syncs a selected item's value with recent changes (if the changed item
   * is selected). Sometimes items are edited when they're not selected, such
   * as when they're edited directly from the item card.
   * @param {*} state
   * @param {
   *  itemID: String,
   *  field: String,
   *  newValue: Any,
   * } changeData
   */
  syncSelectedItemValue(changeData) {
    const { itemID, field, newValue } = changeData;
    for (var i = 0; i < state.selectedItems.length; i++) {
      /** Find a selection with the item's ID if exists to update its values */
      if (state.selectedItems[i]._id === itemID) {
        state.selectedItems[i][field] = newValue;
      }
    }
  },

  /**
   * Syncs an item's value with recent changes.
   * @param {*} state
   * @param {
   *  itemID: String,
   *  field: String,
   *  newValue: Any,
   * } changeData
   */
  syncOriginalItemValue(itemID, field, newValue) {
    for (var i = 0; i < state.items.length; i++) {
      /** Find an item by its ID if exists to sync its value with changes */
      if (state.items[i]._id === itemID) {
        Vue.set(state.items[i], field, newValue); // Vue.set in case field is new to ensure reactivity
      }
    }
  },

  /** ========== ITEM SAVING STATUS MANAGEMENT ========== */
  setItemAsSaving(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].savingInProgress = true;
    }
  },

  unsetItemAsSaving(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].savingInProgress = false;
    }
  },

  /** ========== ITEM SAVING RESULT STATUS ========== */
  setItemSavingFailed(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].savingFailed = true;
    }
  },

  unsetItemSavingFailed(itemID) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].savingFailed = false;
    }
  },

  queueItemUpdate(itemID, updateObject) {
    if (Object.hasOwn(state.editTracker, itemID)) {
      state.editTracker[itemID].savingQueue.push(updateObject);
    }
  },

  /**
   * Updates last modified date if the updated item is selected
   * @param {*} state
   * @param {*} modifiedData
   */
  updateSelectedLastModifiedDate(modifiedData) {
    const { itemID, lastModifiedMS } = modifiedData;

    /** Update lastModifiedMS for selected items */
    for (var i = 0; i < state.selectedItems.length; i++) {
      /** Find a selection with the item's ID if exists to update its values */
      if (state.selectedItems[i]._id === itemID) {
        state.selectedItems[i]["lastModifiedMS"] = lastModifiedMS;
      }
    }
  },

  /**
   * Update last modified date for original item
   * @param {*} state
   * @param {*} modifiedData
   */
  updateLastModifiedDate(itemID, lastModifiedMS) {
    /** Update lastModifiedMS for orginal item */
    for (var i = 0; i < state.items.length; i++) {
      /** Find an item by its ID if exists to sync its value with changes */
      if (state.items[i]._id === itemID) {
        state.items[i]["lastModifiedMS"] = lastModifiedMS;
      }
    }
  },

  /** ========== UPDATE SELECTED ITEM ========== */
  setTitleAsEditing(item) {
    state.editing = true;
    state.editorText = item.name;
  },

  unsetTitleAsEditing() {
    state.editing = false;
    state.editorText = "";
  },

  restoreEditorText(editorBackup) {
    state.editing = true;
    state.editorText = editorBackup.value;
  },

  syncItemEditorContent(newValue) {
    state.editorText = newValue;
  },

  updateItemContent(itemData) {
    const { itemID, newValue } = itemData;
    const itemIndex = state.items.findIndex((item) => item._id === itemID);
    state.items[itemIndex]["name"] = newValue;
  },

  undoUpdateItemContent(backupData) {
    const { itemID, backupContent } = backupData;
    const itemIndex = state.items.findIndex((item) => item._id === itemID);
    state.items[itemIndex]["name"] = backupContent;
  },

  setAllDataForPropertyLoaded(newValue) {
    state.allDataForPropertyLoaded = newValue;
  },

  addNewItemsToList(items) {
    state.items = [...state.items, ...items];
    state.options.skip = state.items.length;
  },

  setItemConfig(type) {
    /* Load based on user's preferences / configurations */
    if (type === "TASK") {
      state.itemConfig.title = true;
      state.itemConfig.checkmark = true;
    } else if (type === "NOTE") {
      state.itemConfig.position = "middle";
      state.itemConfig.title = true;
      state.itemConfig.content = true;
      state.itemConfig.tags = true;
      state.itemConfig.trashBeforeDelete = true;
    }
  },
};

const ItemStore = {
  state,
  methods,
};

export default ItemStore;
